// Lib
import { FC, useCallback, useState } from "react";
// Types
import { EFilterDateValue } from "types/common";
import {
  FilterCustomDate,
  FilterField,
  FilterStateItem,
  TableFilters,
} from "types/tableFilters";

// Theme
import { theme } from "theme";
// Helpers
import {
  getFilterDropdownOptions,
  getFormatedInputPrefix,
  getFormatedValue,
  isTimePicker,
} from "./helpers";
// Icons
import { ArrowDropDownBlackIcon, AddIcon } from "icons";
// Helpers
import { createFiltersDropdown } from "helpers/tableFiltersHelpers";
// Components
import { DropDown } from "components";
import { UsersFilter } from "./UsersFilter";
import { TableDateRangePicker } from "./DateRangePicker";
// Styled
import { FlexContainer } from "styled/Box";
import {
  AddFilterButton,
  CloseIcon,
  FilterSelect,
  FilterInput,
  SelectedFilterContainer,
} from "./styled";

export const TableFiltersBar: FC<TableFilters & { collapsed: boolean }> = ({
  collapsed,
  filters,
  selected,
  setValue,
}) => {
  const [isOpen, setIsOpen] = useState({});

  const setFilterValue = (
    value: string,
    name: string,
    subValue?: FilterCustomDate,
  ) => {
    const filterType = filters.find(filter => filter.key === name)?.field;

    const currentFilter = selected?.find(
      (f: FilterStateItem) => f?.name === name,
    );

    if (filterType === FilterField.INPUT) {
      setValue([
        ...selected.map((f: FilterStateItem) => {
          if (f.name == currentFilter.name) {
            return {
              ...currentFilter,
              value,
              subValue: subValue
                ? { ...currentFilter.subValue, ...subValue }
                : null,
            };
          }
          return f;
        }),
      ]);
    }

    if (filterType === FilterField.MULTISELECT) {
      if (currentFilter.value.includes(value)) {
        const deselected = [
          ...selected.map((f: FilterStateItem) => {
            if (f.name == currentFilter.name) {
              return {
                ...currentFilter,
                value: [...(currentFilter.value as string[])].filter(
                  v => v !== value,
                ),
              };
            }
            return f;
          }),
        ];

        setValue(deselected);
        return;
      }

      setValue([
        ...selected.map((f: FilterStateItem) => {
          if (f.name == currentFilter.name) {
            return {
              ...currentFilter,
              value: [...(currentFilter.value as string[]), value],
            };
          }
          return f;
        }),
      ]);

      return;
    }

    if (filterType === FilterField.DATE_RANGE) {
      setValue(
        selected.map((f: FilterStateItem) => {
          if (f.name == currentFilter.name) {
            return {
              ...currentFilter,
              value,
              subValue: subValue
                ? { ...currentFilter.subValue, ...subValue }
                : null,
            };
          }
          return f;
        }),
      );

      return;
    }

    setValue([
      ...selected.map((f: FilterStateItem) => {
        if (f.name == currentFilter.name) {
          return {
            ...currentFilter,
            value,
            subValue: subValue
              ? { ...currentFilter.subValue, ...subValue }
              : null,
          };
        }
        return f;
      }),
    ]);
  };

  const removeFilter = (name: string) => {
    const newList = selected?.filter(
      (filter: FilterStateItem) => filter?.name !== name,
    );

    setValue(newList);
  };

  const setFilter = filterData => {
    if (!selected?.find((f: FilterStateItem) => f?.name === filterData.key)) {
      const data: FilterStateItem[] = [
        ...selected,
        {
          name: filterData.key,
          value: "",
          field: filters.find(filter => filter.key === filterData.key).field,
        },
      ];

      setValue(data);
    }
  };

  const setDateFilterValue = useCallback(
    (value: string, name: string, subValue?: FilterCustomDate) => {
      setFilterValue(value, name, subValue);
    },
    [selected],
  );

  const dropdownFilters = createFiltersDropdown(filters);

  return (
    <FlexContainer
      style={{
        height: collapsed ? "0px" : "67px",
        transition: "height 0.2s",
      }}
    >
      {!collapsed && !!selected.length && (
        <SelectedFilterContainer>
          {selected.map(({ name, value, field, subValue }) => {
            const isDropDown =
              field === FilterField.SELECT ||
              field === FilterField.MULTISELECT ||
              field === FilterField.DATE_RANGE ||
              field === FilterField.BOOLEAN;

            return (
              <FlexContainer
                $align="center"
                style={{ marginLeft: 10, position: "relative" }}
                key={name}
              >
                {isDropDown && (
                  <FilterSelect
                    $inputType={field}
                    suffixIcon={
                      <FlexContainer $align="center">
                        <ArrowDropDownBlackIcon />
                        <div
                          style={{
                            width: 20,
                            height: 30,
                            borderLeft: `1px solid ${theme.color.borderMedium}`,
                          }}
                        />
                      </FlexContainer>
                    }
                    removeIcon={<CloseIcon />}
                    open={isOpen[name]}
                    onMouseDown={() => setIsOpen({ [name]: true })}
                    onBlur={() => {
                      if (value !== EFilterDateValue.CUSTOM) {
                        setIsOpen({ [name]: false });
                      }
                    }}
                    onChange={v => setFilterValue(v, name)}
                    value={getFormatedValue(filters, name, value, subValue)}
                    options={getFilterDropdownOptions(filters, selected, name)}
                    dropdownRender={menu => {
                      if (isTimePicker(filters, name)) {
                        return (
                          <>
                            {menu}
                            <TableDateRangePicker
                              name={name}
                              selected={selected}
                              setIsOpen={setIsOpen}
                              subValue={subValue}
                              setDateFilterValue={setDateFilterValue}
                            />
                            {/* {renderPicker(name, subValue)} */}
                          </>
                        );
                      }

                      return <>{menu}</>;
                    }}
                  />
                )}

                {field === FilterField.USER_SELECT && (
                  <UsersFilter
                    name={name}
                    isOpen={isOpen[name]}
                    field={field}
                    selected={selected}
                    value={value}
                    setIsOpen={value => setIsOpen({ [name]: value })}
                    setFilterValue={setFilterValue}
                  />
                )}

                {field === FilterField.INPUT && (
                  <FilterInput
                    $inputType={FilterField.INPUT}
                    prefix={getFormatedInputPrefix(filters, name)}
                    value={value}
                    onChange={e => setFilterValue(e.target.value, name)}
                    suffix={
                      <FlexContainer $align="center">
                        <div
                          style={{
                            height: 30,
                            borderLeft: `1px solid ${theme.color.borderMedium}`,
                          }}
                        />
                      </FlexContainer>
                    }
                  />
                )}

                <FlexContainer
                  style={{
                    position: "absolute",
                    right: 4,
                    zIndex: 1,
                  }}
                >
                  <CloseIcon onClick={() => removeFilter(name)} />
                </FlexContainer>
              </FlexContainer>
            );
          })}

          <DropDown
            items={dropdownFilters}
            trigger={["click"]}
            onClick={setFilter}
          >
            <AddFilterButton type="link" icon={<AddIcon />}>
              Add Filter
            </AddFilterButton>
          </DropDown>
        </SelectedFilterContainer>
      )}
    </FlexContainer>
  );
};
